<template>
  <license-layout>
    <template #sideView>
      <license-tier :tier-name="license?.windows?.tierName || ''" />
      <license-info-windows
        :class="{ 'license-info': license?.windows?.type === LICENSE_TYPES.Marketplace }"
        :license-key="license?.key || ''"
        :last-update-time="parseInt(license?.windows?.lastUpdateTime) || '1970-01-01 00:00'"
        :expiration-days-left="license?.expirationInfo?.expirationDate"
        :contract-end-date="license?.expirationInfo?.contractEndDate"
        :is-license-offline="licenseStates[license?.state] === licenseStatesMap.ActiveOffline || licenseStates[license?.state] === licenseStatesMap.ExpiredOffline"
        :is-license-type-free="license?.windows?.type === LICENSE_TYPES.Free"
      />
      <license-deactivate
        v-if="userRolePermissions.authorize?.value?.license.visible.deactivateLicense && license?.windows?.type !== LICENSE_TYPES.Marketplace"
        :is-license-offline="licenseStates[license?.state] === licenseStatesMap.ActiveOffline || licenseStates[license?.state] === licenseStatesMap.ExpiredOffline"
        :is-unix="false"
        @deactivate="deactivateLicense()"
      />
    </template>
    <template #content>
      <div
        class="license-widget-wrapper column inline no-wrap shadowboxing"
        style="min-width: 450px; flex-grow: 1"
      >
        <license-total-detailed-view></license-total-detailed-view>
        <license-addons-features />
      </div>
    </template>
  </license-layout>
  <ib-ok-cancel-dialog
    v-model:value="deactivateDialogIsOpen"
    :dialog-title="$t('license.dialog.deactivate.title')"
    :body="$t('license.dialog.deactivate.body')"
    :checkbox-verify-text="$t('license.dialog.deactivate.verifyText')"
    body-icon="warning"
    dialog-style="warning"
    :ok-label="$t('license.dialog.deactivate.okLabel')"
    @ok="deactivateLicenseConfirmed()"
    @cancel="deactivateLicenseCanceled()"
    @hide="deactivateLicenseCanceled()"
  ></ib-ok-cancel-dialog>
  <ib-ok-cancel-dialog
    v-model:value="deactivationFailedIsOpen"
    :dialog-title="$t('license.dialog.deactivationFailed.title')"
    :body="$t('license.dialog.deactivationFailed.body')"
    dialog-style="danger"
    button-style="failure"
    :ok-label="$t('license.dialog.deactivationFailed.okLabel')"
    :cancel-label="$t('license.dialog.deactivationFailed.cancelLabel')"
    @ok="contactSupport()"
    @cancel="deactivateLicenseFailureCanceled()"
    @hide="deactivateLicenseFailureCanceled()"
  ></ib-ok-cancel-dialog>
</template>
<script>
import IbOkCancelDialog from '@/components/common/dialogs/OkCancelDialog.vue';
import LicenseAddonsFeatures from '@/views/license/licenserComponents/licenseAddonsFeatures/licenseAddonsFeatures.vue';
import { LICENSE_TYPES, licenseStates, licenseStatesCodes, licenseStatesMap } from '@/const/licensePage';
import { LicenseAction_Type, useLicensePageQuery, useLicense_Action_Deactivate_AsyncMutation } from '@/graphql/codegen/generated/graphql';
import { defineComponent, inject } from 'vue';
import { longPolling } from '@/services/PlatfromGQL/longPolling';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { useUserRolePermissions } from '@/services/UserRolePermissions/UserRolePermissions';
import { IKEventHub } from '@/types/injectionKeys';
import LicenseLayout from '@/views/license/licenserComponents/layout/license-layout.vue';
import { useMatomo } from '@/services/matomo/matomo.service';
import licenseTier from '../licenserComponents/licenseTier.vue';
import licenseInfoWindows from './licenseComponentsLinux/licenseInfo.windows.vue';
import licenseDeactivate from '../licenserComponents/licenseDeactivate.vue';
import LicenseTotalDetailedView from '../licenserComponents/licenseTotalDetailedView/licenseTotalDetailedView.vue';

export default defineComponent({
  name: 'License',
  components: {
    LicenseLayout,
    LicenseTotalDetailedView,
    LicenseAddonsFeatures,
    IbOkCancelDialog,
    licenseTier,
    licenseInfoWindows,
    licenseDeactivate,
  },
  setup() {
    const eventHub = inject(IKEventHub);
    const matomo = useMatomo();
    const coordinatorsService = inject(IKCoordinatorsService);
    const userRolePermissions = useUserRolePermissions();

    const GQL_useLicenseInfoQuery = useLicensePageQuery(() => ({
      coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
    }));
    const GQL_deactivateLicense = useLicense_Action_Deactivate_AsyncMutation({});
    return { matomo, GQL_useLicenseInfoQuery, GQL_deactivateLicense, LicenseAction_Type, userRolePermissions, eventHub, licenseStates, licenseStatesMap, LICENSE_TYPES };
  },
  data() {
    return {
      deactivateDialogIsOpen: false,
      deactivationFailedIsOpen: false,
    };
  },
  computed: {
    license() {
      return this.GQL_useLicenseInfoQuery?.result?.value?.ibManagerEntities?.license;
    },
  },

  mounted() {
    this.eventHub?.$emit('closeLeftDrawer');
  },

  methods: {
    deactivateLicense() {
      this.deactivateDialogIsOpen = true;
    },
    async deactivateLicenseConfirmed() {
      try {
        this.deactivateDialogIsOpen = false;
        const refreshLicensePollingId = await this.GQL_deactivateLicense.mutate({ licenseKey: this.license?.key || '' });
        await longPolling.fetch('license_action_asyncResult', refreshLicensePollingId?.data?.license_action_deactivate_async).then(async resultData => {
          const result = resultData?.data?.license_action_asyncResult?.result;
          if (result?.errorCode === 0 && (result?.state === licenseStatesCodes.None || result?.state === licenseStatesCodes.rest)) {
            await this.$router.push({ name: 'enterLicense' });
          } else {
            this.deactivationFailedIsOpen = true;
          }
        });
      } catch (error) {
        this.deactivationFailedIsOpen = true;
      } finally {
        this.matomo?.trackEvent({
          category: 'License Page',
          action: 'Deactivate',
        });
      }
    },
    contactSupport() {
      this.deactivationFailedIsOpen = false;
      const supportMailUrl = process.env.VUE_APP_SUPPORT_MAIL;
      window.open(supportMailUrl, '_blank');
    },
    deactivateLicenseCanceled() {
      this.deactivateDialogIsOpen = false;
    },
    deactivateLicenseFailureCanceled() {
      this.deactivationFailedIsOpen = false;
    },
  },
});
</script>

<style lang="scss" scoped>
.license-info {
  margin-bottom: 0px;
}
.license-widget-wrapper {
  display: flex !important;
  padding: 70px 20px;
  background: $-system-app-white;
}
.license-page {
  gap: 24px;
  min-height: 100%;
  height: max-content;
  max-width: 1672px !important;
}
</style>
